import styled from 'styled-components';
import { CloseIcon } from '../TipSheetIcons/close-icon';
import { formatClusterName } from '../Stories/Stories';
import { parseKeywords, formatKeywords } from '../../utils/keywordUtils';

const FilterChoicesOuter = styled.div`
  display: ${(props) => (props.variant === 'sidebar' ? 'none' : 'block')};
  border-bottom: 1px solid #e8ecef;

  @media (min-width: 769px) {
    display: ${(props) => (props.variant === 'mobile' ? 'none' : 'block')};
  }

  @media (max-width: 768px) {
    display: ${(props) => (props.variant === 'sidebar' ? 'none' : 'block')};
    padding: 1rem 0;
    z-index: initial;
    position: relative;
    top: initial;
    padding: ${(props) => (props.variant === 'mobile-dashboard' ? '1rem' : '0 0 1rem')};
    background-color: #f4f7f9;
  }
`;

const ActiveFiltersCard = styled.div`
  background: #eeeeee;
  border-radius: 6px;
  padding: 0.875rem 1rem 1rem;
  box-sizing: border-box;
`;

const ActiveFiltersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;

  a {
    color: #0a819f;
    text-decoration: none;
    font-weight: 500;
  }

  a:hover {
    color: #086a82;
    text-decoration: underline;
  }
`;

const ActiveFiltersTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #000000;
`;

const ClearAllButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  color: #0a819f;
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: #086a82;
  }

  &:focus {
    outline: 2px solid #0a819f;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const HeaderDivider = styled.div`
  height: 1px;
  background: #d8d8d8;
  margin-bottom: 0.75rem;
`;

const FilterChoicesInternal = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  width: 100%;
  align-items: center;

  @media (max-width: 768px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    width: max-content;
    max-width: none;
    margin: initial;

    &::-webkit-scrollbar {
      display: none;
    }

    scrollbar-width: none;
    -ms-overflow-style: none;
  }
`;

const FilterPill = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding: 0.35rem 0.5rem;
  border: 1px solid #d0d0d0;
  background: #ffffff;

  & svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`;

const PillText = styled.span`
  font-size: 0.875rem;
  color: #666666;
  white-space: nowrap;
  font-style: ${(props) => (props.$isUnquoted ? 'italic' : 'normal')};
`;

const PillCloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  margin-left: 0.5rem;
  padding: 0.125rem;
  display: flex;
  align-items: center;
  color: #666666;

  &:hover {
    color: #000000;
  }

  &:focus {
    outline: 2px solid #0e4c60;
    outline-offset: 2px;
    border-radius: 50%;
  }
`;

export const ArchiveFilterChoices = ({
  tags = [],
  setTags,
  legislators = [],
  setLegislators,
  variant,
  selectedBills = [],
  setSelectedBills,
  keywords = '',
  setKeywords,
  setBillFilterActive = () => {},
  setFilteredStoriesByBill = () => {},
  setSearchActive = () => {},
  searchInputRef,
  chamberFilter = 'both',
  setChamberFilter = () => {},
}) => {
  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
    setSearchActive(true);
  };

  const handleRemoveLegislator = (legislatorId) => {
    setLegislators(
      legislators.filter((legislator) => {
        if (typeof legislator === 'string') return legislator !== legislatorId;
        if (legislator.pid !== undefined) return legislator.pid !== legislatorId;
        if (legislator.value !== undefined) return legislator.value !== legislatorId;
        return true;
      })
    );
    setSearchActive(true);
  };

  const handleRemoveBill = (billValue) => {
    const updatedBills = selectedBills.filter((bill) => {
      if (typeof bill === 'string') return bill !== billValue;
      if (bill.value !== undefined) return bill.value !== billValue;
      return true;
    });
    setSelectedBills(updatedBills);
    if (updatedBills.length === 0) {
      setBillFilterActive(false);
      setFilteredStoriesByBill([]);
    }
    setSearchActive(true);
  };

  const handleClearAll = () => {
    setTags([]);
    setLegislators([]);
    setSelectedBills([]);
    setKeywords('');
    if (setChamberFilter) setChamberFilter('both');
    setBillFilterActive(false);
    setFilteredStoriesByBill([]);
    setSearchActive(true);
    if (searchInputRef?.current) {
      searchInputRef.current.value = '';
      try {
        const ev = new Event('input', { bubbles: true });
        searchInputRef.current.dispatchEvent(ev);
      } catch (e) {
      }
    }
  };

  const handleRemoveKeyword = (keywordToRemove) => {
    const keywordsArray = parseKeywords(keywords);
    const updatedArray = keywordsArray.filter((keyword) => keyword !== keywordToRemove);
    const updatedKeywords = formatKeywords(updatedArray);

    setKeywords(updatedKeywords);
    setSearchActive(true);

    if (searchInputRef?.current) {
      searchInputRef.current.value = '';
      try {
        const ev = new Event('input', { bubbles: true });
        searchInputRef.current.dispatchEvent(ev);
      } catch (e) {
      }
    }
  };

  const formatLegislatorName = (legislator) => {
    if (typeof legislator === 'string') return legislator;
    return `${legislator.first} ${legislator.last} (${legislator.party[0]})`;
  };

  const keywordsArray = parseKeywords(keywords);

  const activeFilterCount =
    tags.length +
    legislators.length +
    selectedBills.length +
    keywordsArray.length +
    (chamberFilter !== 'both' ? 1 : 0);

  if (activeFilterCount === 0) return null;

  return (
    <FilterChoicesOuter variant={variant}>
      <ActiveFiltersCard>
        <ActiveFiltersHeader>
          <ActiveFiltersTitle>Active Filters ({activeFilterCount})</ActiveFiltersTitle>
          <ClearAllButton type="button" onClick={handleClearAll} aria-label="Clear all filters">
            Clear All
          </ClearAllButton>
        </ActiveFiltersHeader>
        <HeaderDivider aria-hidden="true" />
        <FilterChoicesInternal>
          {tags.map((tag) => (
            <FilterPill key={`tag-${tag}`}>
              <PillText>{formatClusterName(tag)}</PillText>
              <PillCloseButton onClick={() => handleRemoveTag(tag)} aria-label={`Remove ${tag} tag filter`}>
                <CloseIcon />
              </PillCloseButton>
            </FilterPill>
          ))}

          {legislators.map((legislator) => {
            const isString = typeof legislator === 'string';
            const legislatorId = isString
              ? legislator
              : legislator.pid !== undefined
              ? legislator.pid
              : legislator.value;
            const legislatorName = isString
              ? legislator
              : legislator.label
              ? legislator.label
              : formatLegislatorName(legislator);

            return (
              <FilterPill key={`legislator-${legislatorId}`}>
                <PillText>{legislatorName}</PillText>
                <PillCloseButton
                  onClick={() => handleRemoveLegislator(legislatorId)}
                  aria-label={`Remove ${legislatorName} filter`}
                >
                  <CloseIcon />
                </PillCloseButton>
              </FilterPill>
            );
          })}

          {selectedBills.map((bill) => {
            const billLabel = typeof bill === 'string' ? bill : bill.label || bill.value;
            const billValue = typeof bill === 'string' ? bill : bill.value;
            return (
              <FilterPill key={`bill-${billValue}`}>
                <PillText>{billLabel}</PillText>
                <PillCloseButton
                  onClick={() => handleRemoveBill(billValue)}
                  aria-label={`Remove ${billLabel} bill filter`}
                >
                  <CloseIcon />
                </PillCloseButton>
              </FilterPill>
            );
          })}

          {keywordsArray.map((keyword, index) => {
            const isQuoted = /^".*"$/.test(keyword);
            return (
              <FilterPill key={`keyword-${keyword}-${index}`}>
                <PillText $isUnquoted={!isQuoted}>{keyword}</PillText>
                <PillCloseButton
                  onClick={() => handleRemoveKeyword(keyword)}
                  aria-label={`Remove ${keyword} from search`}
                >
                  <CloseIcon />
                </PillCloseButton>
              </FilterPill>
            );
          })}
        </FilterChoicesInternal>
      </ActiveFiltersCard>
    </FilterChoicesOuter>
  );
};
